import { ExposureItemMediaModel, ExposureItemModel, LessonPlanModel, MediaSlot, MediaType } from "@/models";
import { RemoteTeachingService } from "@/services";
import { notification } from "ant-design-vue";
import { fmtMsg } from "vue-glcommonui";
import { ErrorLocale } from "@/locales/localeid";
import { ActionContext, ActionTree } from "vuex";
import {
  ContentRootTypeFromValue,
  Exposure,
  ExposureItem,
  ExposureItemMedia,
  ExposureStatus,
  ExposureTypeFromValue,
  LessonState,
  TeachingActivityTarget,
  ExposureType,
} from "./state";
import { fromReader } from "@/utils/exposure-item-generator";
import {
  DEFAULT_ALTERNATE_MEDIA_BLOCK_ITEM_NAME,
  DEFAULT_CONTENT_BLOCK_ITEM_NAME,
  DEFAULT_RESOLUTION,
  DEFAULT_TEACHING_ACTIVITY_BLOCK_ITEM_NAME,
  getContentSignature,
} from "./utils";
import { ExposureDetailType, getExposureFirstSlide } from "@/views/teacher-class/components/lesson-plan/lesson-plan";
import { defineVuexName, vuexName, MODULES } from "@/store/utils";
import { ToggleTargetsModel, ToggleTargetModel } from "@/ws";
import { calculatePlayedTime } from "@/utils/convertDuration";
import MediaItemLpComplete from "@/assets/images/lp-complete.png";
import MediaItemTransition from "@/assets/images/transition.png";
import { DEFAULT_LESSON_IMAGE_URL } from "@/utils/fabric-utils";

export enum StorageContainerEnum {
  Video = 0, // for video (mp4)
  Audio = 1, // for audio (mp3)
  Page = 2, // for images (jpg, png)
  Pdf = 3, // for pdf
}

const actions: ActionTree<LessonState, any> = {
  //setInfo
  async [defineVuexName(MODULES.LESSON.DISPATCHES.SET_INFO)](
    store: ActionContext<LessonState, any>,
    payload: { lessonPlan: LessonPlanModel; isSetCurrentExposure: boolean },
  ) {
    if (!payload) return;
    const signature = await getContentSignature({ userCountryCode: store.rootGetters[vuexName(MODULES.AUTH.GETTERS.GET_USER_COUNTRY_CODE)] });
    const { url, data } = signature;
    const buildUrl = (src: string, type: StorageContainerEnum): string => {
      const containerData = data.find((item) => item.type === type);
      if (!containerData) return "";
      const { container, signature } = containerData;
      return `${url}${container}/${src}${signature}`;
    };
    const exposures: Array<Exposure> = [];
    const imagesToPreLoad: string[] = [];
    for (const e of payload.lessonPlan.contents) {
      const items: Array<ExposureItem> = e.contents.map((c: ExposureItemModel) => {
        const media: Array<ExposureItemMedia> = c.page.map((p: ExposureItemMediaModel) => {
          const imageUrl = buildUrl(p.url, StorageContainerEnum.Page);
          imagesToPreLoad.push(imageUrl);
          return {
            id: p.id,
            image: {
              url: imageUrl,
              width: p.resolution ? parseInt(p.resolution.split("X")[0]) : parseInt(DEFAULT_RESOLUTION.split("X")[0]),
              height: p.resolution ? parseInt(p.resolution.split("X")[1]) : parseInt(DEFAULT_RESOLUTION.split("X")[1]),
            },
            teacherUseOnly: p.teacherUseOnly,
            exposureType: ExposureDetailType.VCP_BLOCK,
          };
        });
        return {
          id: c.id,
          name: c.title,
          media: media,
        };
      });

      const newPage = e.page ? e.page.map((p: any) => ({ ...p, page: [{ ...p }] })) : [];
      const contentBlockItems: Array<ExposureItem> = newPage.map((c: any) => {
        const media: Array<ExposureItemMedia> = c.page.map((p: any) => {
          const imageUrl = buildUrl(p.url, StorageContainerEnum.Page);
          imagesToPreLoad.push(imageUrl);
          return {
            id: p.id,
            image: {
              url: imageUrl,
              width: p.resolution ? parseInt(p.resolution.split("X")[0]) : parseInt(DEFAULT_RESOLUTION.split("X")[0]),
              height: p.resolution ? parseInt(p.resolution.split("X")[1]) : parseInt(DEFAULT_RESOLUTION.split("X")[1]),
            },
            teacherUseOnly: p.teacherUseOnly,
            exposureType: ExposureDetailType.CONTENT_BLOCK,
          };
        });
        return {
          id: c.id,
          name: DEFAULT_CONTENT_BLOCK_ITEM_NAME,
          media: media,
          teacherUseOnly: c.teacherUseOnly,
          isClicked: false,
        };
      });
      const includedIndependentMode = e.readerContents && Array.isArray(e.readerContents) && e.readerContents.length > 0;
      //handle alternate media block
      const alternateMediaBlockItems: ExposureItem[][] = [];
      if (!includedIndependentMode) {
        const newMedia = e.media ? e.media : [];
        for (const i of newMedia) {
          // do not show Thumbnail and Audio Background media slots in lesson plan container
          if (i.mediaSlotId === MediaSlot.Thumbnail || i.mediaSlotId === MediaSlot.AudioBackground) {
            continue;
          }

          // handle audio background slot
          const audioBackground =
            i.mediaSlotId === MediaSlot.Audio
              ? newMedia
                  .find((media) => media.mediaSlotId === MediaSlot.AudioBackground)
                  ?.page.map((p) => {
                    const imageUrl = buildUrl(p.url, StorageContainerEnum.Page);
                    imagesToPreLoad.push(imageUrl);
                    return {
                      ...p,
                      image: {
                        url: imageUrl,
                        width: p.resolution ? parseInt(p.resolution.split("X")[0]) : parseInt(DEFAULT_RESOLUTION.split("X")[0]),
                        height: p.resolution ? parseInt(p.resolution.split("X")[1]) : parseInt(DEFAULT_RESOLUTION.split("X")[1]),
                      },
                    };
                  })
                  .sort((a, b) =>
                    a.pageTimingToMillisecond && b.pageTimingToMillisecond && a.pageTimingToMillisecond > b.pageTimingToMillisecond ? 1 : -1,
                  )
              : undefined;

          // handle thumbnail slot
          const thumbnailSlot =
            i.mediaSlotId === MediaSlot.StreamingVideo ? newMedia.find((media) => media.mediaSlotId === MediaSlot.Thumbnail) : undefined;
          const thumbnail = thumbnailSlot?.url ? buildUrl(thumbnailSlot?.url, StorageContainerEnum.Page) : undefined;
          if (thumbnail) {
            imagesToPreLoad.push(thumbnail);
          }
          const alternateMedia: Array<ExposureItemMedia> = [
            {
              id: i.id,
              image: {
                url: DEFAULT_LESSON_IMAGE_URL,
                audioBackground,
                thumbnail,
                mediaBlobUrl: i.url,
              },
              teacherUseOnly: false,
              exposureType: ExposureDetailType.ALTERNATE_MEDIA_BLOCK,
            },
          ];
          const alternateMediaItems: Array<ExposureItem> = [
            {
              id: i.id,
              name: DEFAULT_ALTERNATE_MEDIA_BLOCK_ITEM_NAME,
              media: alternateMedia,
              mediaTypeId: i.mediaTypeId,
              mediaSlotId: i.mediaSlotId,
              teacherUseOnly: false,
            },
          ];
          const newImg = i.page ? i.page.map((p: any) => ({ ...p, page: [{ ...p }] })) : [];
          const alternateImgItems: Array<ExposureItem> = newImg.map((c: any) => {
            const media: Array<ExposureItemMedia> = c.page.map((p: any) => {
              const imageUrl = buildUrl(p.url, StorageContainerEnum.Page);
              imagesToPreLoad.push(imageUrl);
              return {
                id: p.id,
                image: {
                  url: imageUrl,
                  width: p.resolution ? parseInt(p.resolution.split("X")[0]) : parseInt(DEFAULT_RESOLUTION.split("X")[0]),
                  height: p.resolution ? parseInt(p.resolution.split("X")[1]) : parseInt(DEFAULT_RESOLUTION.split("X")[1]),
                },
                teacherUseOnly: p.teacherUseOnly,
                exposureType: ExposureDetailType.ALTERNATE_MEDIA_BLOCK,
              };
            });
            return {
              id: c.id,
              name: DEFAULT_ALTERNATE_MEDIA_BLOCK_ITEM_NAME,
              media: media,
              teacherUseOnly: c.teacherUseOnly,
              mediaTypeId: undefined,
            };
          });
          const item = alternateImgItems.concat(alternateMediaItems);
          alternateMediaBlockItems.push(item);
        }
      }

      //handle teaching activity block
      const newContentExposureTeachingActivity = e.contentExposureTeachingActivity
        ? e.contentExposureTeachingActivity?.map((c: any) => ({
            ...c,
            page: [
              {
                id: c.teachingActivityId,
                resolution: DEFAULT_RESOLUTION,
                sequence: c.sequence,
                url: c.imageUrl,
                metaData: JSON.parse(c.metaData),
              },
            ],
          }))
        : [];
      const teachingActivityBlockItems: Array<ExposureItem> = newContentExposureTeachingActivity?.map((c: any) => {
        const media: Array<ExposureItemMedia> = c.page.map((p: any) => {
          const url = p.url ? buildUrl(p.url, StorageContainerEnum.Page) : "";
          imagesToPreLoad.push(url);
          return {
            id: p.id, // need to confirm is contentExposureId or teachingActivity.id
            image: {
              url,
              width: p.resolution ? parseInt(p.resolution.split("X")[0]) : parseInt(DEFAULT_RESOLUTION.split("X")[0]),
              height: p.resolution ? parseInt(p.resolution.split("X")[1]) : parseInt(DEFAULT_RESOLUTION.split("X")[1]),
              metaData: p.metaData,
            },
            teacherUseOnly: e.page.find((page) => page.id === c.pageId)?.teacherUseOnly,
            exposureType: ExposureDetailType.TEACHING_ACTIVITY_BLOCK,
          };
        });
        return {
          id: c.contentExposureId,
          name: c.imageName || DEFAULT_TEACHING_ACTIVITY_BLOCK_ITEM_NAME,
          media,
          textContent: c?.teachingActivity?.text,
        };
      });
      const readerItems = includedIndependentMode ? fromReader(e.readerContents, buildUrl, imagesToPreLoad) : [];
      const exposure: Exposure = {
        id: e.id,
        name: e.title,
        duration: e.maxDuration,
        status: e.played ? ExposureStatus.COMPLETED : ExposureStatus.DEFAULT,
        type: ExposureTypeFromValue(e.contentType.id, e.contentType.name),
        includedIndependentMode,
        thumbnailURL: e.thumbnailUrl ? buildUrl(e.thumbnailUrl, StorageContainerEnum.Page) : "",
        contentRootType: ContentRootTypeFromValue(e.contentRootType),
        //* related slides
        items,
        //* component slides
        contentBlockItems: includedIndependentMode ? readerItems : contentBlockItems,
        //* teaching activities slides
        teachingActivityBlockItems,
        //* alternative slides
        alternateMediaBlockItems,
      };
      exposures.push(exposure);
    }

    const stopSpin = () => {
      if (store.rootGetters[vuexName(MODULES.SPIN.GETTERS.GET_SPLASH)]) {
        store.commit(vuexName(MODULES.SPIN.COMMITS.SET_SPLASH), false, { root: true });
      }
    };

    if (exposures.findIndex((e) => e.type === ExposureType.COMPLETE) > -1) {
      imagesToPreLoad.push(MediaItemLpComplete);
    }

    if (exposures.findIndex((e) => e.type === ExposureType.TRANSITION) > -1) {
      imagesToPreLoad.push(MediaItemTransition);
    }

    const imageCount = imagesToPreLoad.length;
    if (imageCount == 0) {
      stopSpin();
    }

    let loadedCount = 0,
      errorCount = 0;

    const checkAllLoaded = () => {
      if (loadedCount + errorCount == imageCount) {
        stopSpin();
      }
    };

    const onload = async () => {
      loadedCount++;
      checkAllLoaded();
    };

    const onerror = async () => {
      errorCount++;
      checkAllLoaded();
    };

    imagesToPreLoad.forEach((im_url) => {
      const img = new Image();
      img.src = im_url;
      img.crossOrigin = "anonymous";
      img.onload = onload;
      img.onerror = onerror;
    });

    store.commit(vuexName(MODULES.LESSON.COMMITS.SET_EXPOSURES, false), { exposures: exposures });
    const playedTime = calculatePlayedTime(exposures);
    store.commit(vuexName(MODULES.LESSON.COMMITS.SET_PLAYED_TIME, false), { time: playedTime });
    store.commit(vuexName(MODULES.LESSON.COMMITS.SET_TOTAL_TIME, false), { time: payload.lessonPlan.totalTime });
    if (payload.isSetCurrentExposure)
      await store.dispatch(vuexName(MODULES.LESSON.DISPATCHES.SET_BOTH_EXPOSURE_AND_SLIDE, false), payload.lessonPlan);
  },
  //setBothExposureAndSlide
  [defineVuexName(MODULES.LESSON.DISPATCHES.SET_BOTH_EXPOSURE_AND_SLIDE)]({ state, commit, getters }, ls: LessonPlanModel) {
    commit(vuexName(MODULES.LESSON.COMMITS.SET_CURRENT_EXPOSURE, false), {
      id: ls.selectedExposureId,
      preventSelectFirstSlideAutomatically: true,
    });
    const exposureItemId = ls.selectedExposureItemId;
    const exposure = state.exposures.find((ex) => ex.id === ls.selectedExposureId);
    if (exposure) {
      const exposureFirstSlide = getExposureFirstSlide(exposure, getters[vuexName(MODULES.LESSON.GETTERS.GET_ALTERNATIVE_SHOWN, false)]);
      commit(vuexName(MODULES.LESSON.COMMITS.SET_CURRENT_EXPOSURE_ITEM_MEDIA, false), {
        id: exposureItemId || exposureFirstSlide?.id,
        exposureType: exposureItemId ? undefined : exposureFirstSlide?.exposureType,
      });
    }
  },
  //getAlternateMediaUrl
  async [defineVuexName(MODULES.LESSON.DISPATCHES.GET_ALTERNATE_MEDIA_URL)](
    store: ActionContext<LessonState, any>,
    payload: { id: string; mediaBlobUrl: string; mediaTypeId: MediaType },
  ) {
    if (!payload.mediaBlobUrl) {
      notification.error({ message: fmtMsg(ErrorLocale.AlternateMediaUrlError, { field: "mediaBlobUrl" }) });
      return;
    }
    if (payload.mediaTypeId === MediaType.none) {
      notification.error({ message: fmtMsg(ErrorLocale.AlternateMediaUrlError, { field: "mediaTypeId" }) });
      return;
    }
    const mediaTypeToContainerType: Partial<Record<MediaType, StorageContainerEnum>> = {
      [MediaType.mp4]: StorageContainerEnum.Video,
      [MediaType.mp3]: StorageContainerEnum.Audio,
      [MediaType.pdf]: StorageContainerEnum.Pdf,
      [MediaType.png]: StorageContainerEnum.Page,
    };
    const containerType = mediaTypeToContainerType[payload.mediaTypeId];
    if (containerType === undefined) {
      notification.error({ message: fmtMsg(ErrorLocale.AlternateMediaUrlError, { field: "mediaTypeId" }) });
      return;
    }
    const signature = await getContentSignature({ userCountryCode: store.rootGetters[vuexName(MODULES.AUTH.GETTERS.GET_USER_COUNTRY_CODE)] });
    const { url, data } = signature;
    const containerData = data.find((item) => item.type === containerType);
    if (!containerData) {
      notification.error({ message: fmtMsg(ErrorLocale.AlternateMediaUrlError, { field: "containerData" }) });
      return;
    }
    const mediaUrl = `${url}${containerData.container}/${payload.mediaBlobUrl}${containerData.signature}`;
    store.commit(vuexName(MODULES.LESSON.COMMITS.SET_ALTERNATE_MEDIA_URL, false), { id: payload.id, url: mediaUrl });
  },
  [defineVuexName(MODULES.LESSON.DISPATCHES.PROCESS_VISIBLE_TARGETS_DATA_FROM_API)](
    { commit }: ActionContext<LessonState, any>,
    payload: TeachingActivityTarget[] | undefined,
  ) {
    const transformedTags = payload?.filter((target) => target.visible).map((target) => target.tag) || [];
    commit(vuexName(MODULES.LESSON.COMMITS.SET_VISIBLE_TARGET_TAGS, false), transformedTags);
  },
  [defineVuexName(MODULES.LESSON.DISPATCHES.PROCESS_VISIBLE_TARGET_BY_FROM_MESSAGE)](
    { commit, state }: ActionContext<LessonState, any>,
    payload: TeachingActivityTarget[],
  ) {
    const visibleTags = new Set(state.visibleTargetTags);

    payload.forEach((target) => {
      if (target.visible) {
        visibleTags.add(target.tag);
      } else {
        visibleTags.delete(target.tag);
      }
    });

    const newTags = Array.from(visibleTags);
    commit(vuexName(MODULES.LESSON.COMMITS.SET_VISIBLE_TARGET_TAGS, false), newTags);
  },
  // Only teacher and student can call this action, helper can't call this action
  async [defineVuexName(MODULES.LESSON.DISPATCHES.TOGGLE_VISIBLE_TARGET)](
    { commit, state, rootState, rootGetters }: ActionContext<LessonState, any>,
    payload: string,
  ) {
    const wasVisible = state.visibleTargetTags.includes(payload);
    const newTags = wasVisible ? state.visibleTargetTags.filter((tag) => tag !== payload) : [...state.visibleTargetTags, payload];
    commit(vuexName(MODULES.LESSON.COMMITS.SET_VISIBLE_TARGET_TAGS, false), newTags);

    const sessionId = rootGetters[vuexName(MODULES.CLASS_TEACHING.GETTERS.GET_SESSION_ID)];

    const teacher = rootState.teacherRoom?.teacher;
    const student = rootState.studentRoom.student;
    const studentModeOneId = rootGetters[vuexName(MODULES.STUDENT_ROOM.GETTERS.GET_STUDENT_MODE_ONE_ID)];
    const studentModeHelperOneId = rootGetters[vuexName(MODULES.STUDENT_ROOM.GETTERS.GET_STUDENT_MODE_HELPER_ONE_ID)];
    const userId = teacher?.id || student?.id || "";
    const isOneOne = teacher
      ? !!rootGetters[vuexName(MODULES.TEACHER_ROOM.GETTERS.GET_STUDENT_MODE_ONE_ID)]
      : student
      ? studentModeOneId === student.id || studentModeHelperOneId === student.id
      : false;

    const model: ToggleTargetModel = {
      visible: !wasVisible,
      tag: payload,
    };

    const request: ToggleTargetsModel = {
      sessionId,
      userId,
      targets: [model],
      isOneOne,
    };

    await RemoteTeachingService.toggleTargets(request);
  },
  // Only for teacher, helper and student can't call this action
  async [defineVuexName(MODULES.LESSON.DISPATCHES.TOGGLE_ALL_VISIBLE_TARGETS)](
    { commit, rootState, rootGetters }: ActionContext<LessonState, any>,
    {
      isShowAll,
      tags,
    }: {
      isShowAll: boolean;
      tags: string[];
    },
  ) {
    commit(vuexName(MODULES.LESSON.COMMITS.SET_VISIBLE_TARGET_TAGS, false), isShowAll ? tags : []);

    const sessionId = rootGetters[vuexName(MODULES.CLASS_TEACHING.GETTERS.GET_SESSION_ID)];
    const userId: string = rootState.teacherRoom?.teacher?.id || "";
    const isTeacherOneOne = !!rootGetters[vuexName(MODULES.TEACHER_ROOM.GETTERS.GET_STUDENT_MODE_ONE_ID)];
    const targets: ToggleTargetModel[] = tags.map((tag) => {
      return {
        visible: isShowAll,
        tag: tag,
        sessionId,
      };
    });

    const request: ToggleTargetsModel = {
      sessionId: sessionId,
      userId: userId,
      targets: targets,
      isOneOne: isTeacherOneOne,
    };

    await RemoteTeachingService.toggleTargets(request);
  },
};

export default actions;
